import { TextField } from "@mui/material";
import { useState } from "react";
import { formatAmount, sanitizeAmountInput } from "@/utils/number-format";

/**
 * A number input that reads like money — 1,234.56 — while keeping a plain number in state.
 *
 * A native `type="number"` cannot show separators at all (the browser rejects a value containing
 * commas), so this is a text field that formats on blur and shows the raw digits while focused.
 * Formatting as you type would fight the caret on every keystroke; swapping on focus avoids that
 * entirely, and the field is only ever unformatted while someone is looking at it.
 *
 * `value` is always the unformatted string — what a caller submits, with no separators to strip.
 */
export default function AmountField({
  value,
  onChange,
  label = "Amount",
  helperText,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const n = Number(value);
  const display = focused || value === "" || !Number.isFinite(n) ? value : formatAmount(n);

  return (
    <TextField
      size="small"
      label={label}
      value={display}
      disabled={disabled}
      helperText={helperText}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(sanitizeAmountInput(e.target.value))}
      slotProps={{ htmlInput: { inputMode: "decimal", style: { textAlign: "right" } } }}
    />
  );
}
